import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getRules, createRule, updateRule, deleteRule, getSymbols, getRuleFirings, getRuleTemplates, fmtPrice } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, X, Zap, TrendingUp, TrendingDown, History, ChevronDown, Sparkles } from "lucide-react";
import { toast } from "sonner";

const FiringLog = ({ ruleId }) => {
  const { data = [], isLoading } = useQuery({
    queryKey: ["firings", ruleId],
    queryFn: () => getRuleFirings(ruleId),
    refetchInterval: 15000,
  });
  if (isLoading) return <div className="text-[10px] font-mono text-tertiary px-1 py-2">Loading history…</div>;
  if (!data.length) return <div className="text-[10px] font-mono text-tertiary px-1 py-2">No triggers yet.</div>;
  return (
    <div className="space-y-1 py-1 max-h-40 overflow-y-auto" data-testid={`firing-log-${ruleId}`}>
      {data.map((f, i) => (
        <div key={i} className="flex items-center justify-between text-[10px] font-mono px-2 py-1 rounded bg-app border border-border-subtle">
          <span className="text-white font-bold">{f.symbol}</span>
          <span className="text-secondary">${fmtPrice(f.price)}</span>
          <span className={f.change_pct >= 0 ? "text-bull" : "text-bear"}>{f.change_pct >= 0 ? "+" : ""}{f.change_pct}%</span>
          <span className="text-tertiary">{new Date(f.created_at).toLocaleTimeString()}</span>
        </div>
      ))}
    </div>
  );
};

const METRICS = [
  { id: "price", label: "Price ($)" },
  { id: "change_pct", label: "Change (%)" },
  { id: "rsi", label: "RSI" },
  { id: "rel_volume", label: "Rel Volume (x)" },
  { id: "volume", label: "Volume" },
];
const OPS = [
  { id: "gte", label: "≥" },
  { id: "gt", label: ">" },
  { id: "lte", label: "≤" },
  { id: "lt", label: "<" },
];
const OP_SYM = { gte: "≥", gt: ">", lte: "≤", lt: "<" };
const M_LABEL = { price: "price", change_pct: "chg%", rsi: "RSI", rel_volume: "rel vol", volume: "vol" };

const STRATEGY_OPTS = [
  { id: "bull_call_spread", label: "Bull Call Spread" },
  { id: "bear_put_spread", label: "Bear Put Spread" },
  { id: "long_straddle", label: "Long Straddle" },
  { id: "long_strangle", label: "Long Strangle" },
  { id: "iron_condor", label: "Iron Condor" },
];
const STRAT_LABEL = Object.fromEntries(STRATEGY_OPTS.map((s) => [s.id, s.label]));

const blankCond = () => ({ metric: "change_pct", op: "gte", value: 5 });

export const RulesPanel = () => {
  const qc = useQueryClient();
  const { data: rules = [] } = useQuery({ queryKey: ["rules"], queryFn: getRules, refetchInterval: 10000 });
  const { data: symbols = [] } = useQuery({ queryKey: ["symbols"], queryFn: getSymbols });
  const { data: templates = [] } = useQuery({ queryKey: ["rule-templates"], queryFn: getRuleTemplates });

  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("ANY");
  const [bias, setBias] = useState("bullish");
  const [conds, setConds] = useState([blankCond()]);
  const [openId, setOpenId] = useState(null);
  const [ruleType, setRuleType] = useState("metric");
  const [strategy, setStrategy] = useState("bull_call_spread");
  const [stratOp, setStratOp] = useState("lte");
  const [stratValue, setStratValue] = useState(5);

  const create = useMutation({
    mutationFn: createRule,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rules"] });
      toast.success("Alert rule created");
      setName(""); setSymbol("ANY"); setBias("bullish"); setConds([blankCond()]);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not create rule"),
  });
  const toggle = useMutation({
    mutationFn: ({ id, active }) => updateRule(id, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rules"] }),
  });
  const remove = useMutation({
    mutationFn: deleteRule,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["rules"] }); toast.success("Rule deleted"); },
  });

  const updateCond = (i, patch) => setConds((c) => c.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const addCond = () => conds.length < 3 && setConds((c) => [...c, blankCond()]);
  const removeCond = (i) => setConds((c) => c.filter((_, idx) => idx !== i));

  const submit = () => {
    if (ruleType === "strategy") {
      if (symbol === "ANY") { toast.error("Pick a specific ticker for a strategy rule"); return; }
      const val = parseFloat(stratValue);
      if (Number.isNaN(val) || val <= 0) { toast.error("Enter a debit/credit threshold > 0"); return; }
      create.mutate({
        name: name || `${symbol} ${STRAT_LABEL[strategy]}`, rule_type: "strategy",
        symbol, strategy, strat_op: stratOp, strat_value: val, bias, conditions: [], active: true,
      });
      return;
    }
    const clean = conds.map((c) => ({ ...c, value: parseFloat(c.value) }));
    const bad = clean.find((c) => Number.isNaN(c.value));
    if (bad) { toast.error("Every condition needs a number value"); return; }
    const trivial = clean.find((c) => ["price", "volume", "rel_volume"].includes(c.metric) && c.value <= 0);
    if (trivial) { toast.error("Price / volume conditions must be greater than 0"); return; }
    create.mutate({ name: name || `${symbol} rule`, rule_type: "metric", symbol, bias, conditions: clean, active: true });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="rules-panel">
      {/* Builder */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden self-start">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-app/60">
          <Zap size={15} className="text-ai" />
          <h3 className="text-sm font-semibold uppercase tracking-[0.1em] text-white">Create Alert Rule</h3>
        </div>
        <div className="p-4 space-y-4">
          {templates.length > 0 && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-tertiary font-mono flex items-center gap-1 mb-2">
                <Sparkles size={11} className="text-ai" /> One-tap starters
              </label>
              <div className="flex flex-wrap gap-1.5">
                {templates.map((t) => (
                  <button key={t.id} data-testid={`template-${t.id}`} title={t.description}
                    onClick={() => create.mutate({ name: t.name, active: true, ...t.rule })}
                    className="text-[11px] font-mono px-2.5 py-1.5 rounded border border-ai/25 bg-ai/5 text-ai hover:bg-ai/15 transition-colors">
                    + {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-tertiary font-mono">Rule name</label>
            <Input data-testid="rule-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. PLTR breakout" className="bg-app border-border mt-1 text-sm" />
          </div>

          <div className="flex gap-1 p-1 bg-app rounded border border-border">
            {[["metric", "Metric"], ["strategy", "Strategy"]].map(([id, lbl]) => (
              <button key={id} data-testid={`ruletype-${id}`} onClick={() => { setRuleType(id); if (id === "strategy" && symbol === "ANY") setSymbol(symbols[0]?.symbol || "AAPL"); if (id === "metric") setSymbol("ANY"); }}
                className={`flex-1 text-xs font-mono py-1.5 rounded transition-colors ${ruleType === id ? "bg-neutral text-white" : "text-secondary hover:bg-surface-2"}`}>
                {lbl}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-tertiary font-mono">Ticker</label>
              <Select value={symbol} onValueChange={setSymbol}>
                <SelectTrigger data-testid="rule-symbol" className="bg-app border-border mt-1 font-mono text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-border max-h-64">
                  {ruleType === "metric" && <SelectItem value="ANY">Any ticker</SelectItem>}
                  {symbols.map((s) => (
                    <SelectItem key={s.symbol} value={s.symbol} className="font-mono">{s.symbol} · {s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-tertiary font-mono">Bias</label>
              <Select value={bias} onValueChange={setBias}>
                <SelectTrigger data-testid="rule-bias" className="bg-app border-border mt-1 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-border">
                  <SelectItem value="bullish">Bullish → CALL</SelectItem>
                  <SelectItem value="bearish">Bearish → PUT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {ruleType === "metric" ? (
            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest text-tertiary font-mono">Conditions (all must be true)</label>
              {conds.map((c, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`rule-cond-${i}`}>
                  <Select value={c.metric} onValueChange={(v) => updateCond(i, { metric: v })}>
                    <SelectTrigger className="bg-app border-border text-sm flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-border">
                      {METRICS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={c.op} onValueChange={(v) => updateCond(i, { op: v })}>
                    <SelectTrigger className="bg-app border-border text-sm w-16 font-mono"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-border">
                      {OPS.map((o) => <SelectItem key={o.id} value={o.id} className="font-mono">{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input data-testid={`rule-value-${i}`} type="number" value={c.value}
                    onChange={(e) => updateCond(i, { value: e.target.value })}
                    className="bg-app border-border text-sm w-24 font-mono text-right" />
                  {conds.length > 1 && (
                    <button onClick={() => removeCond(i)} className="text-tertiary hover:text-bear"><X size={15} /></button>
                  )}
                </div>
              ))}
              {conds.length < 3 && (
                <button data-testid="add-condition" onClick={addCond}
                  className="flex items-center gap-1 text-[11px] font-mono text-ai hover:text-white uppercase">
                  <Plus size={12} /> Add condition
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest text-tertiary font-mono">Watch a spread's net debit / credit</label>
              <Select value={strategy} onValueChange={setStrategy}>
                <SelectTrigger data-testid="rule-strategy" className="bg-app border-border text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-border">
                  {STRATEGY_OPTS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-secondary flex-1">net cost is</span>
                <Select value={stratOp} onValueChange={setStratOp}>
                  <SelectTrigger data-testid="rule-strat-op" className="bg-app border-border text-sm w-16 font-mono"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-border">
                    <SelectItem value="lte" className="font-mono">≤</SelectItem>
                    <SelectItem value="gte" className="font-mono">≥</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative w-28">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-tertiary font-mono text-sm">$</span>
                  <Input data-testid="rule-strat-value" type="number" step="0.1" value={stratValue}
                    onChange={(e) => setStratValue(e.target.value)}
                    className="bg-app border-border text-sm font-mono text-right pl-5" />
                </div>
              </div>
              <p className="text-[11px] text-tertiary leading-snug">
                e.g. {symbol} {STRAT_LABEL[strategy]} debit {stratOp === "lte" ? "≤" : "≥"} ${stratValue} → get pinged.
              </p>
            </div>
          )}

          <button data-testid="create-rule-btn" onClick={submit} disabled={create.isPending}
            className="w-full bg-neutral hover:bg-neutral/80 text-white font-semibold text-sm py-2.5 rounded transition-colors disabled:opacity-50">
            {create.isPending ? "Creating…" : "Create rule"}
          </button>
        </div>
      </div>

      {/* Active rules */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden self-start">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-app/60">
          <h3 className="text-sm font-semibold uppercase tracking-[0.1em] text-white">Your Rules</h3>
          <span className="text-[10px] font-mono text-tertiary">({rules.length})</span>
        </div>
        {!rules.length && (
          <div className="text-center py-10 text-tertiary font-mono text-xs px-4">
            No rules yet. Build one like "PLTR price ≥ 80 & rel vol ≥ 2".
          </div>
        )}
        {rules.map((r) => (
          <div key={r.id} data-testid={`rule-row-${r.id}`} className="px-4 py-3 border-b border-border-subtle">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-white text-sm">{r.name}</span>
                <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border flex items-center gap-0.5 ${r.bias === "bullish" ? "bg-bull/10 text-bull border-bull/25" : "bg-bear/10 text-bear border-bear/25"}`}>
                  {r.bias === "bullish" ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
                  {r.bias === "bullish" ? "CALL" : "PUT"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Switch data-testid={`rule-toggle-${r.id}`} checked={r.active}
                  onCheckedChange={(v) => toggle.mutate({ id: r.id, active: v })} />
                <button data-testid={`rule-delete-${r.id}`} onClick={() => remove.mutate(r.id)}
                  className="text-tertiary hover:text-bear"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-mono text-[11px] text-secondary bg-app px-1.5 py-0.5 rounded border border-border-subtle">
                {r.symbol}
              </span>
              {r.rule_type === "strategy" ? (
                <span className="font-mono text-[11px] text-ai bg-app px-1.5 py-0.5 rounded border border-ai/25">
                  {STRAT_LABEL[r.strategy] || r.strategy} net {OP_SYM[r.strat_op] || "≤"} ${r.strat_value}
                </span>
              ) : (
                r.conditions.map((c, i) => (
                  <span key={i} className="font-mono text-[11px] text-secondary bg-app px-1.5 py-0.5 rounded border border-border-subtle">
                    {M_LABEL[c.metric]} {OP_SYM[c.op]} {c.value}{c.metric === "change_pct" ? "%" : c.metric === "rel_volume" ? "x" : ""}
                  </span>
                ))
              )}
            </div>
            {r.last_fired && (
              <div className="text-[10px] font-mono text-tertiary mt-1.5">
                Last fired {new Date(r.last_fired).toLocaleTimeString()}
              </div>
            )}
            <button data-testid={`rule-history-${r.id}`} onClick={() => setOpenId(openId === r.id ? null : r.id)}
              className="flex items-center gap-1 text-[10px] font-mono text-ai hover:text-white uppercase mt-1.5">
              <History size={11} /> History
              <ChevronDown size={11} className={`transition-transform ${openId === r.id ? "rotate-180" : ""}`} />
            </button>
            {openId === r.id && <FiringLog ruleId={r.id} />}
          </div>
        ))}
      </div>
    </div>
  );
};
